import {
	getImagePreviewActions,
	SharePointDocumentActionContext
} from "client/Features/Documents/SharePoint/actions";
import { DocumentItemViewModel, FileViewModel } from "client/Api/Documents/SharePoint/common";
import { ImageFile, ImagePreviewDialog } from "client/Features/Documents/Dialogs/ImagePreview/ImagePreviewDialog";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { getDownloadFileUrl } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { Action } from "client_legacy/ndaw/actions";
import { useAlerts } from "client/Hooks";
import { ensureNotNil } from "client/Misc/utils";
import { DocumentStoreType } from "client/Api/Documents/common";
import { useAddBookmarkDialog } from "../../Bookmarks/AddBookmarkDialog";

export type SharePointImagePreviewDialogProps = {
	initialImage: FileViewModel;
	allImageItems: readonly FileViewModel[];
	onClose: () => void;
	currentImageChangedCallback?: (file: FileViewModel) => void;
	initialContext?: SharePointDocumentActionContext;
};

export const SharePointImagePreviewDialog = (props: SharePointImagePreviewDialogProps): ReactElement => {
	const {
		initialImage,
		allImageItems,
		onClose,
		currentImageChangedCallback,
		initialContext
	} = props;

	const [currentFileId, setCurrentFileId] = useState<number>(initialImage.id);

	const [addBookmarkDialog, openAddBookmarkDialog] = useAddBookmarkDialog();
	const alertManager = useAlerts();

	const currentFile: FileViewModel = useMemo(
		() => {
			const file: FileViewModel | undefined = allImageItems.find((image) => image.id === currentFileId);

			return ensureNotNil(file);
		},
		[allImageItems, currentFileId]
	);

	useEffect(
		(): void => {
			currentImageChangedCallback?.(ensureNotNil(currentFile));
		},
		[currentFile, currentImageChangedCallback]
	);

	const allImages: ImageFile[] = useMemo(
		(): ImageFile[] => {
			return allImageItems.map((item) => convertToImageFile(item));
		},
		[allImageItems]
	);

	const currentImage = useMemo(
		(): ImageFile => convertToImageFile(ensureNotNil(currentFile)),
		[currentFile]
	);

	const context = useMemo(
		(): SharePointDocumentActionContext | undefined => {
			if (!initialContext || !currentFile) {
				return undefined;
			}

			return {
				...initialContext,
				selection: [currentFile]
			};
		},
		[currentFile, initialContext]
	);

	const addBookmarkHandler = useCallback(
		(item: DocumentItemViewModel): void => {
			openAddBookmarkDialog({
				store: DocumentStoreType.SharePoint,
				item
			});
		},
		[openAddBookmarkDialog]
	);

	const actions = useMemo(
		(): readonly Action<SharePointDocumentActionContext>[] => {
			return getImagePreviewActions(
				alertManager,
				addBookmarkHandler
			);
		},
		[alertManager, addBookmarkHandler]
	);

	const changeImageHandler = (image: ImageFile): void => {
		const item: FileViewModel | undefined = allImageItems.find(x => x.id === image.id);
		setCurrentFileId(
			ensureNotNil(item?.id)
		);
	};

	return (
		<>
			<ImagePreviewDialog
				onClose={onClose}
				currentImage={currentImage}
				allImages={allImages}
				context={context}
				actions={actions}
				onActiveImageChanged={changeImageHandler}
			/>

			{addBookmarkDialog}
		</>
	);
};

SharePointImagePreviewDialog.displayName = "SharePointImagePreviewDialog";

function convertToImageFile(file: FileViewModel): ImageFile {
	return {
		id: file.id,
		name: file.name,
		url: getFileUrl(file)
	};
}

function getFileUrl(file: FileViewModel): string {
	return getDownloadFileUrl(file.id, file.location);
}
